import React, { useMemo } from "react";
import { Input, Pagination } from "@components";
import { formatUseYn } from "@utils/formatUseYn";

const AccountListSection = ({
  data,
  isAdmin,
  companyName,
  onOpenCompanyModal,
  onResetCompany,
  canWrite,
  onOpenSaveModal,
  onOpenEditModal,
  canDelete,
  onDelete,
  selectedItems,
  isAllChecked,
  handleSelectAll,
  handleSelectItem,
  handlePageChange,
  changePageSize,
  pageIndex,
  recordCountPerPage,
  roleOptions,
}) => {
  // state-free: 상세는 부모의 모달로 처리

  const totalCount = data?.paginationInfo?.totalRecordCount ?? data?.resultList?.length ?? 0;

  const roleNameById = useMemo(() => {
    const map = new Map();
    (roleOptions ?? []).forEach((opt) => map.set(opt.key, opt.name));
    return map;
  }, [roleOptions]);

  return (
    <>
      <div className="search-wrap">
        <div className="search-row">
          <div className="search-item">
            <label>회사</label>
            <Input
              name="compName"
              disabled
              value={companyName || ""}
            />
            {isAdmin && (
              <>
                <button type="button" onClick={onOpenCompanyModal} className="search-icon" />
                {onResetCompany && (
                  <button type="button" className="btn-cancel" onClick={onResetCompany}>
                    초기화
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="section-tit-wrap">
        <h3 className="border-left-primary !text-[19px] md:!text-[21px]">
          &nbsp;계정 목록
          <span className="total-txt !text-[15px] md:!text-[16px]">
            {" "}
            | 총 <span className="red">{totalCount}</span>건
          </span>
        </h3>

        <div className="btn-right-box">
          <ul>
            {canWrite && (
              <li>
                <button type="button" className="btn-write" onClick={onOpenSaveModal}>
                  추가
                </button>
              </li>
            )}
            {canDelete && (
              <li>
                <button type="button" className="btn-delete" onClick={onDelete}>
                  삭제
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Mobile: 축약 테이블 + 상세 모달 */}
      <div className="md:hidden table-container overflow-x-auto table-list-wrapper [-webkit-overflow-scrolling:touch]">
        <table className="table-list min-w-max !text-[15px]" style={{ width: "100%" }}>
          <colgroup>
            <col style={{ width: "1%" }} />
            <col style={{ width: "45%" }} />
            <col style={{ width: "40%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input type="checkbox" checked={isAllChecked} onChange={handleSelectAll} />
              </th>
              <th>아이디</th>
              <th>이름</th>
              <th>사용</th>
            </tr>
          </thead>
          <tbody>
            {data?.resultList && data.resultList.length > 0 ? (
              data.resultList.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => {
                    onOpenEditModal?.(item);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                    />
                  </td>
                  <td className="font-mono">
                    {item.id}
                  </td>
                  <td>{item.name}</td>
                  <td>{formatUseYn(item.useYn)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="table-empty" style={{ textAlign: "center" }}>
                  조회 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Desktop: 전체 테이블 */}
      <div className="hidden md:block table-container overflow-x-auto table-list-wrapper">
        <table
          className="table-list w-full !text-[15px] md:!text-[16px] lg:!text-[17px]"
          style={{ width: "100%", tableLayout: "fixed", whiteSpace: "normal", wordBreak: "break-word" }}
        >
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input type="checkbox" checked={isAllChecked} onChange={handleSelectAll} />
              </th>
              <th style={{ width: 56 }}>순번</th>
              <th style={{ width: "16%", textAlign: "left" }}>회사</th>
              <th style={{ width: "12%", textAlign: "left" }}>아이디</th>
              <th style={{ width: "10%", textAlign: "left" }}>이름</th>
              <th style={{ width: "12%" }}>전화번호</th>
              <th style={{ width: "22%", textAlign: "left" }}>이메일</th>
              <th style={{ width: "10%", textAlign: "left" }}>권한</th>
              <th style={{ width: "6%" }}>사용</th>
            </tr>
          </thead>
          <tbody>
            {data?.resultList && data.resultList.length > 0 ? (
              data.resultList.map((item, idx) => (
                <tr
                  key={item.id}
                  onClick={() => onOpenEditModal?.(item)}
                  style={{ cursor: "pointer" }}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                    />
                  </td>
                  <td>{(pageIndex - 1) * recordCountPerPage + idx + 1}</td>
                  <td style={{ textAlign: "left" }}>{item.compName}</td>
                  <td className="font-mono" style={{ textAlign: "left" }}>{item.id}</td>
                  <td style={{ textAlign: "left" }}>{item.name}</td>
                  <td>{item.phoneNo}</td>
                  <td style={{ textAlign: "left" }}>{item.email}</td>
                  <td style={{ textAlign: "left" }}>{roleNameById.get(item.roleId) || item.roleId}</td>
                  <td>{formatUseYn(item.useYn)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="table-empty" style={{ textAlign: "center" }}>
                  조회 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
        <span style={{ fontSize: '14px', whiteSpace: 'nowrap', color: '#555' }}>페이지당 행 수</span>
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto' }}
          value={recordCountPerPage}
          onChange={(e) => changePageSize(Number(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {data?.paginationInfo && data.paginationInfo.totalRecordCount > 0 && (
        <Pagination
          totalPages={Math.ceil(data.paginationInfo.totalRecordCount / data.paginationInfo.recordCountPerPage)}
          currentPage={pageIndex}
          onPageChange={handlePageChange}
          pageRangeDisplayed={10}
        />
      )}
    </>
  );
};

export default AccountListSection;
